import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateAppointmentStatusThunk, updatePaymentStatusThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import apiAxios from "../../../services/apiAxios";
import RescheduleModal from "./RescheduleModal";
import CancelAppointmentModal from "./CancelAppointmentModal";
import HealthMapIcon from "../../common/icons/HealthMapIcon";
import StudioIcon from "../../common/icons/StudioIcon";
import { formatDateString, getTodayString } from "../../../utils/helpers";

// Clean Outlined Icons
const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StethoscopeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.684a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CashIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const ReceiptIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.025 10.025 0 013.122-.063c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-2.49 4.132M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
  </svg>
);

const LiveQueueMonitor = ({ appointments, queueStats, isPinB, onOpenReceiptMaker }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedDate = useSelector((state) => state.appointment.selectedDate);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showRevenue, setShowRevenue] = useState(false);

  // Normalize date string comparison (handles YYYY-MM-DD or ISO strings)
  const normalizeDate = (d) => {
    if (!d) return "";
    const str = String(d).trim();
    if (str.includes("T")) return str.split("T")[0];
    if (str.includes(" ")) return str.split(" ")[0];
    return str;
  };

  const targetDate = normalizeDate(selectedDate);

  // Filter appointments for the selected OPD date
  let dateAppointments = appointments.filter((a) => normalizeDate(a.appointmentDate) === targetDate);

  // Robust Fallback: If strict date string match returns 0 items but appointments exist in state,
  // use appointments so active consultations & queue never show blank on initial load/refresh/login!
  if (dateAppointments.length === 0 && appointments.length > 0) {
    dateAppointments = appointments;
  }

  const waitingAppointments = dateAppointments.filter((a) => a.status === "CHECKED_IN");
  const inConsultationAppointments = dateAppointments.filter((a) => a.status === "IN_CONSULTATION");
  const scheduledAppointments = dateAppointments.filter((a) => a.status === "SCHEDULED");
  const completedAppointments = dateAppointments.filter((a) => a.status === "COMPLETED");

  const getScheduledTitle = (dateStr) => {
    const norm = normalizeDate(dateStr) || getTodayString();
    const todayStr = getTodayString();
    if (norm === todayStr) return "Today's Scheduled";

    const [ty, tm, td] = todayStr.split("-").map(Number);
    const todayDt = new Date(ty, tm - 1, td);

    const yestDt = new Date(todayDt);
    yestDt.setDate(yestDt.getDate() - 1);
    if (norm === formatDateString(yestDt)) return "Yesterday's Scheduled";

    const tomDt = new Date(todayDt);
    tomDt.setDate(tomDt.getDate() + 1);
    if (norm === formatDateString(tomDt)) return "Tomorrow's Scheduled";

    const parts = norm.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]} Scheduled`;
    }
    return `${norm} Scheduled`;
  };

  const getDateContextLabel = (prefix) => {
    const norm = normalizeDate(selectedDate) || getTodayString();
    const todayStr = getTodayString();
    if (norm === todayStr) return `${prefix} Today`;

    const [ty, tm, td] = todayStr.split("-").map(Number);
    const todayDt = new Date(ty, tm - 1, td);

    const yestDt = new Date(todayDt);
    yestDt.setDate(yestDt.getDate() - 1);
    if (norm === formatDateString(yestDt)) return `${prefix} Yesterday`;

    const tomDt = new Date(todayDt);
    tomDt.setDate(tomDt.getDate() + 1);
    if (norm === formatDateString(tomDt)) return `${prefix} Tomorrow`;

    const parts = norm.split("-");
    if (parts.length === 3) {
      return `${prefix} (${parts[2]}/${parts[1]})`;
    }
    return `${prefix} (${norm})`;
  };

  const handleOpenHealthMap = (appointment) => {
    if (!appointment) return;
    const patientContext = {
      PID: appointment.PID || `PID-${appointment.patientPhone}`,
      name: appointment.patientName,
      phoneNumber: appointment.patientPhone,
      gender: appointment.patientGender || "Male",
      age: appointment.patientAge,
      city: appointment.city,
      pincode: appointment.pincode,
    };
    sessionStorage.setItem("healthmap-patient-context", JSON.stringify(patientContext));
    navigate("/healthmap");
  };

  const handleStatusUpdate = (AID, newStatus) => {
    dispatch(
      updateAppointmentStatusThunk({
        AID,
        status: newStatus,
        appointmentDate: selectedDate,
      })
    );
  };

  const handleTogglePayment = (appointment) => {
    const nextStatus = appointment.paymentStatus === "PAID" ? "UNPAID" : "PAID";
    dispatch(
      updatePaymentStatusThunk({
        AID: appointment.AID,
        paymentStatus: nextStatus,
        paymentMethod: appointment.paymentMethod || "CASH",
        consultationFee: appointment.consultationFee,
        appointmentDate: selectedDate,
      })
    );
  };

  const handleStartConsultationInStudio = async (appointment) => {
    if (!appointment) return;

    // Only dispatch status update & show toast if patient was not ALREADY in consultation
    if (appointment.status !== "IN_CONSULTATION") {
      handleStatusUpdate(appointment.AID, "IN_CONSULTATION");
      toast.success("Status updated to IN CONSULTATION");
    }

    let firstName = appointment.patientName || appointment.patient?.name || "";
    let surname = appointment.patientSurname || appointment.patient?.surname || "";
    let cityVal = appointment.patientCity || appointment.city || appointment.patient?.city || appointment.patient?.patientAddress?.city || "";
    let pincodeVal = appointment.patientPincode || appointment.pincode || appointment.patient?.pincode || appointment.patient?.patientAddress?.pincode || "";

    // If city/pincode or surname is missing, do an instant central lookup via API
    if ((!cityVal && !pincodeVal) || !surname) {
      try {
        const query = appointment.patientPhone || appointment.PID;
        if (query) {
          const res = await apiAxios.get(`api/auth/search-customer-data?search=${query}`);
          const matchedList = res.data?.data || res.data?.customers || res.data || [];
          if (Array.isArray(matchedList) && matchedList.length > 0) {
            const match = matchedList.find((c) => c.phoneNumber === appointment.patientPhone || c.PID === appointment.PID) || matchedList[0];
            if (match) {
              if (!surname && match.surname) surname = match.surname;
              if (!cityVal) cityVal = match.city || match.patientAddress?.city || "";
              if (!pincodeVal) pincodeVal = match.pincode || match.patientAddress?.pincode || "";
              if (!firstName) firstName = match.name || "";
            }
          }
        }
      } catch (err) {
        console.error("Error fetching patient details for Studio auto-fill:", err);
      }
    }

    // Accurately split First Name & Surname if still unsplit
    if (!surname && firstName.includes(" ")) {
      const parts = firstName.trim().split(/\s+/);
      firstName = parts[0] || "";
      surname = parts.slice(1).join(" ") || "";
    }

    const patientContext = {
      PID: appointment.PID || appointment.patient?.PID || `PID-${appointment.patientPhone}`,
      name: firstName,
      surname: surname,
      phoneNumber: appointment.patientPhone || appointment.patient?.phoneNumber || "",
      gender: appointment.patientGender || appointment.patient?.gender || "Male",
      age: appointment.patientAge || appointment.patient?.age || "",
      dateofbirth: appointment.patientDob || appointment.patient?.dateofbirth || "",
      city: cityVal,
      pincode: pincodeVal,
      AID: appointment.AID,
      visitType: appointment.visitType,
    };
    sessionStorage.setItem("healthmap-patient-context", JSON.stringify(patientContext));

    if (appointment.patientPhone || appointment.PID) {
      navigate(`/studio?phone=${appointment.patientPhone || ""}&pid=${appointment.PID || ""}&aid=${appointment.AID}`);
    } else {
      navigate("/studio");
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Stat Cards Banner */}
      <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-5 gap-2.5 sm:gap-3 lg:gap-3.5">
        <div className="col-span-1 md:col-span-2 lg:col-span-1 bg-white p-3 lg:p-3.5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-2 lg:gap-2.5 min-w-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold shrink-0">
            <ListIcon />
          </div>
          <div className="min-w-0">
            <span className="text-lg lg:text-xl font-black text-gray-900 block truncate">{queueStats.totalToday || 0}</span>
            <p className="text-[10px] lg:text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">
              {getDateContextLabel("Total")}
            </p>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-1 bg-white p-3 lg:p-3.5 rounded-2xl border border-amber-100 shadow-sm flex items-center gap-2 lg:gap-2.5 min-w-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold shrink-0">
            <ClockIcon />
          </div>
          <div className="min-w-0">
            <span className="text-lg lg:text-xl font-black text-amber-600 block truncate">
              {queueStats.waitingCount || 0}
            </span>
            <p className="text-[10px] lg:text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">
              Waiting Room
            </p>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-1 bg-white p-3 lg:p-3.5 rounded-2xl border border-purple-100 shadow-sm flex items-center gap-2 lg:gap-2.5 min-w-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold shrink-0">
            <StethoscopeIcon />
          </div>
          <div className="min-w-0">
            <span className="text-lg lg:text-xl font-black text-purple-600 block truncate">
              {inConsultationAppointments.length || queueStats.inConsultationCount || 0}
            </span>
            <p className="text-[10px] lg:text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">
              In Consultation
            </p>
          </div>
        </div>

        <div className="col-span-1 md:col-span-3 lg:col-span-1 bg-white p-3 lg:p-3.5 rounded-2xl border border-emerald-100 shadow-sm flex items-center gap-2 lg:gap-2.5 min-w-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0">
            <CheckCircleIcon />
          </div>
          <div className="min-w-0">
            <span className="text-lg lg:text-xl font-black text-emerald-600 block truncate">
              {queueStats.completedToday || 0}
            </span>
            <p className="text-[10px] lg:text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">
              Completed
            </p>
          </div>
        </div>

        <div className="col-span-2 md:col-span-3 lg:col-span-1 bg-white p-3 lg:p-3.5 rounded-2xl border border-emerald-100 shadow-sm flex items-center justify-between gap-1.5 min-w-0 overflow-hidden">
          {showRevenue ? (
            <div className="flex items-center gap-2 lg:gap-2.5 min-w-0 overflow-hidden">
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold shrink-0">
                <CashIcon />
              </div>
              <div className="min-w-0">
                <span className="text-base lg:text-lg font-black text-emerald-700 tracking-wide block truncate">
                  ₹{queueStats.totalRevenue || 0}
                </span>
                <p className="text-[10px] lg:text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">
                  {getDateContextLabel("Collected")}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 lg:gap-2.5 select-none min-w-0 overflow-hidden">
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-gray-100/80 text-gray-300 flex items-center justify-center font-bold shrink-0">
                <CashIcon />
              </div>
              <div className="min-w-0">
                <span className="text-base lg:text-lg font-black text-gray-300 tracking-widest block leading-none truncate">
                  ••••••
                </span>
                <p className="text-[10px] lg:text-[11px] font-semibold text-gray-300 uppercase tracking-wider mt-0.5 truncate">
                  Hidden
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowRevenue((prev) => !prev)}
            className="p-1 rounded-xl bg-gray-100 hover:bg-emerald-100 text-gray-500 hover:text-emerald-800 transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
            title={showRevenue ? "Hide collected revenue info" : "Show collected revenue info"}
          >
            {showRevenue ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </div>

      {/* 2. Active Consultation Banner(s) */}
      {inConsultationAppointments.length > 0 ? (
        <div className="space-y-4">
          {inConsultationAppointments.length > 1 && (
            <div className="flex items-center justify-between px-2">
              <span className="text-xs font-black uppercase tracking-wider text-purple-900 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-600 animate-pulse"></span>
                Active Consultations ({inConsultationAppointments.length} Patients in Cabin)
              </span>
            </div>
          )}
          {inConsultationAppointments.map((apt) => (
            <div
              key={apt.AID}
              className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-950 p-6 rounded-3xl text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-purple-700/50"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex flex-col items-center justify-center border border-white/20 shrink-0">
                  <span className="text-xs uppercase tracking-widest text-purple-200 font-bold">
                    Token
                  </span>
                  <span className="text-2xl font-black text-white">
                    #{apt.tokenNumber}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-500/30 text-purple-200 border border-purple-400/30 animate-pulse">
                      ● Currently In Consultation
                    </span>
                    <span className="text-xs text-purple-200 font-medium">
                      {apt.slotStartTime} - {apt.slotEndTime}
                    </span>
                  </div>
                  <h3 className="text-2xl font-black mt-1">
                    {apt.patientName} {apt.patientSurname || ""}
                  </h3>
                  <p className="text-xs text-purple-200 mt-0.5">
                    Phone: {apt.patientPhone} | Age: {apt.patientAge || "N/A"} | Gender: {apt.patientGender || "N/A"} | Mode: {apt.visitType}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <button
                  type="button"
                  onClick={() => onOpenReceiptMaker(apt)}
                  className="flex-1 md:flex-none px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs backdrop-blur-md border border-white/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ReceiptIcon /> Issue Receipt
                </button>

                {isPinB && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleOpenHealthMap(apt)}
                      className="flex-1 md:flex-none px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                      title="Open Patient's HealthMap"
                    >
                      <HealthMapIcon height={16} width={16} fill="#ffffff" /> HealthMap
                    </button>

                    <button
                      type="button"
                      onClick={() => handleStartConsultationInStudio(apt)}
                      className="flex-1 md:flex-none px-5 py-2.5 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <StudioIcon height={16} width={16} fill="#ffffff" /> EMR Studio
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => handleStatusUpdate(apt.AID, "COMPLETED")}
                  className="flex-1 md:flex-none px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  Mark Completed
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 border-2 border-dashed border-gray-200 p-6 rounded-3xl flex items-center justify-between text-gray-500">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200/60 text-gray-600 flex items-center justify-center font-bold">
              <StethoscopeIcon />
            </div>
            <div>
              <h4 className="font-bold text-gray-800 text-sm">No Active Patient in Doctor's Cabin</h4>
              <p className="text-xs text-gray-500">Select a waiting patient below to call into consultation</p>
            </div>
          </div>
        </div>
      )}

      {/* 3. Queue Lists Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Waiting Room Column */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              Waiting Room ({waitingAppointments.length})
            </h3>
            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
              Checked-In
            </span>
          </div>

          <div className="space-y-3">
            {waitingAppointments.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-xs font-semibold">
                No patients currently waiting.
              </div>
            ) : (
              waitingAppointments.map((app) => (
                <div
                  key={app.AID}
                  className="p-4 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50/50 via-white to-amber-50/20 shadow-2xs hover:shadow-md transition-all space-y-3 relative overflow-hidden group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className="shrink-0 px-2 py-0.5 rounded-lg bg-amber-500 text-white font-black text-[11px] shadow-2xs">
                        #{app.tokenNumber}
                      </span>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-900 text-xs truncate leading-snug">
                          {app.patientName}
                        </h4>
                        <p className="text-[11px] text-gray-500 font-medium mt-0.5 flex items-center gap-1 truncate">
                          <span>📞 {app.patientPhone}</span>
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleTogglePayment(app)}
                      className={`shrink-0 text-[10px] font-extrabold px-2.5 py-1 rounded-full cursor-pointer transition-all shadow-2xs ${
                        app.paymentStatus === "PAID"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100"
                          : "bg-rose-50 text-rose-700 border border-rose-200/80 hover:bg-rose-100"
                      }`}
                      title="Click to toggle payment status"
                    >
                      ₹{app.consultationFee || 0} · {app.paymentStatus === "PAID" ? "✓ PAID" : "UNPAID"}
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 bg-gray-50/80 px-2.5 py-1 rounded-xl border border-gray-100/80">
                    <span className="flex items-center gap-1">
                      ⏰ <span className="font-bold text-gray-800">{app.slotStartTime}</span>
                    </span>
                    {app.visitType && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800 bg-amber-100/70 px-2 py-0.5 rounded-md">
                        {app.visitType}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t border-amber-100/60">
                    <div className="grid grid-cols-2 gap-1.5">
                      {isPinB ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenHealthMap(app)}
                            className="w-full py-1.5 px-2 text-[11px] font-bold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                            title="Open Patient's HealthMap"
                          >
                            HealthMap
                          </button>

                          <button
                            type="button"
                            onClick={() => setRescheduleTarget(app)}
                            className="w-full py-1.5 px-2 text-[11px] font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                            title="Reschedule Appointment"
                          >
                            Reschedule
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setRescheduleTarget(app)}
                          className="col-span-2 w-full py-1.5 px-2 text-[11px] font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center text-center"
                          title="Reschedule Appointment"
                        >
                          Reschedule
                        </button>
                      )}

                      <button
                        onClick={() => onOpenReceiptMaker(app)}
                        className="w-full py-1.5 px-2 text-[11px] font-bold text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                      >
                        Receipt
                      </button>

                      <button
                        type="button"
                        onClick={() => setCancelTarget(app)}
                        className="w-full py-1.5 px-2 text-[11px] font-bold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                        title="Cancel Appointment"
                      >
                        Cancel
                      </button>
                    </div>

                    <button
                      onClick={() => handleStatusUpdate(app.AID, "IN_CONSULTATION")}
                      className="w-full py-2 text-xs font-extrabold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-[0.98]"
                    >
                      Call to Cabin →
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Scheduled Appointments Column */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
              {getScheduledTitle(selectedDate)} ({scheduledAppointments.length})
            </h3>
            <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
              Upcoming
            </span>
          </div>

          <div className="space-y-3">
            {scheduledAppointments.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-xs font-semibold">
                No upcoming appointments.
              </div>
            ) : (
              scheduledAppointments.map((app) => (
                <div
                  key={app.AID}
                  className="p-4 rounded-2xl border border-gray-200/80 bg-gradient-to-br from-blue-50/20 via-white to-gray-50/30 shadow-2xs hover:shadow-md transition-all space-y-3 overflow-hidden group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className="shrink-0 px-2 py-0.5 rounded-lg bg-blue-600 text-white font-black text-[11px] shadow-2xs">
                        #{app.tokenNumber}
                      </span>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-900 text-xs truncate leading-snug">
                          {app.patientName}
                        </h4>
                        <p className="text-[11px] text-gray-500 font-medium mt-0.5 flex items-center gap-1 truncate">
                          <span>📞 {app.patientPhone}</span>
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleTogglePayment(app)}
                      className={`shrink-0 text-[10px] font-extrabold px-2.5 py-1 rounded-full cursor-pointer transition-all shadow-2xs ${
                        app.paymentStatus === "PAID"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100"
                          : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                      }`}
                      title="Click to toggle payment status"
                    >
                      ₹{app.consultationFee || 0} · {app.paymentStatus === "PAID" ? "✓ PAID" : "UNPAID"}
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 bg-gray-50/80 px-2.5 py-1 rounded-xl border border-gray-100/80">
                    <span className="flex items-center gap-1">
                      ⏰ <span className="font-bold text-gray-800">{app.slotStartTime}</span>
                    </span>
                    {app.visitType && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-blue-800 bg-blue-50 px-2 py-0.5 rounded-md">
                        {app.visitType}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-1.5">
                      {isPinB ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenHealthMap(app)}
                            className="w-full py-1.5 px-2 text-[11px] font-bold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                            title="Open Patient's HealthMap"
                          >
                            HealthMap
                          </button>

                          <button
                            type="button"
                            onClick={() => setRescheduleTarget(app)}
                            className="w-full py-1.5 px-2 text-[11px] font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                            title="Reschedule Appointment"
                          >
                            Reschedule
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setRescheduleTarget(app)}
                          className="col-span-2 w-full py-1.5 px-2 text-[11px] font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center text-center"
                          title="Reschedule Appointment"
                        >
                          Reschedule
                        </button>
                      )}

                      <button
                        onClick={() => onOpenReceiptMaker(app)}
                        className="w-full py-1.5 px-2 text-[11px] font-bold text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                      >
                        Receipt
                      </button>

                      <button
                        type="button"
                        onClick={() => setCancelTarget(app)}
                        className="w-full py-1.5 px-2 text-[11px] font-bold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center justify-center"
                        title="Cancel Appointment"
                      >
                        Cancel
                      </button>
                    </div>

                    <button
                      onClick={() => handleStatusUpdate(app.AID, "CHECKED_IN")}
                      className="w-full py-2 text-xs font-extrabold text-amber-950 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 rounded-xl shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-[0.98]"
                    >
                      Check-In Patient →
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Completed Appointments Column */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              Completed ({completedAppointments.length})
            </h3>
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              Finished
            </span>
          </div>

          <div className="space-y-3">
            {completedAppointments.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-xs font-semibold">
                No completed consultations yet today.
              </div>
            ) : (
              completedAppointments.map((app) => (
                <div
                  key={app.AID}
                  className="p-3.5 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/30 via-white to-emerald-50/10 shadow-2xs hover:shadow-sm transition-all space-y-2.5 overflow-hidden group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="shrink-0 px-2 py-0.5 rounded-lg bg-emerald-600 text-white font-black text-[11px] shadow-2xs">
                        #{app.tokenNumber}
                      </span>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-900 text-xs truncate leading-snug">
                          {app.patientName}
                        </h4>
                        <p className="text-[11px] text-gray-500 font-medium truncate">
                          📞 {app.patientPhone}
                        </p>
                      </div>
                    </div>

                    <span className="text-[10px] font-black text-emerald-800 bg-emerald-100/90 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0 shadow-2xs">
                      ✓ Done
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-between pt-2 border-t border-emerald-100/60 gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        onClick={() => onOpenReceiptMaker(app)}
                        className="text-[11px] font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 px-2.5 py-1 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95"
                      >
                        Receipt
                      </button>

                      {isPinB && (
                        <button
                          type="button"
                          onClick={() => handleOpenHealthMap(app)}
                          className="text-[11px] font-bold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200/80 px-2.5 py-1 rounded-xl transition-all cursor-pointer shadow-2xs active:scale-95"
                          title="Open Patient's HealthMap"
                        >
                          HealthMap
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Reschedule Appointment Modal */}
      {rescheduleTarget && (
        <RescheduleModal
          appointment={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
        />
      )}

      {/* Cancel Appointment Modal */}
      {cancelTarget && (
        <CancelAppointmentModal
          appointment={cancelTarget}
          onClose={() => setCancelTarget(null)}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default LiveQueueMonitor;
